import round from 'lodash/round';

import { Skeleton, Typography, Stack } from '@cast/design-system';

import { useIsLoading } from 'core/loading';
import { getResourceUsagePercentage } from 'utils/node';

import { MetricRow } from '../_components/MetricRow';
import { EphemeralStorageMetrics } from '../types';

type Props = {
  metrics: EphemeralStorageMetrics;
  testId?: string;
};

export const EphemeralStorageMetricsSection = ({ metrics, testId }: Props) => {
  const isLoading = useIsLoading();
  const acronym = 'GiB';
  const full = 'EPHEMERAL STORAGE';
  const showRequestsNotSet = metrics.hasRequests === false;
  const usedPercentage =
    getResourceUsagePercentage(metrics.used, metrics.allocatable) ?? 0;

  return (
    <Stack data-testid={testId}>
      <Stack direction="row" justifyContent="space-between" mb={16}>
        <Typography variant="L12B">{full}</Typography>

        {!isLoading && (
          <Typography variant="P14B">
            {`${metrics.capacity} ${acronym}`}
          </Typography>
        )}
      </Stack>

      {!isLoading ? (
        <Stack gap={8}>
          {showRequestsNotSet ? (
            <>
              <MetricRow
                label="Reqs."
                progressValue={0}
                progressColor="blue.200"
                primaryValue="Not set"
              />
              <MetricRow
                label="Used"
                progressValue={usedPercentage}
                progressColor="green.300"
                tooltipTitle={`Used ${acronym} out of Allocatable ${acronym}`}
                primaryValue={`${metrics.used} ${acronym}`}
                secondaryValue={` ${round(usedPercentage)} %`}
              />
            </>
          ) : (
            <>
              <MetricRow
                label="Reqs."
                progressValue={metrics.usage}
                progressColor="blue.200"
                tooltipTitle={`Requested ${acronym} out of Allocatable ${acronym}`}
                primaryValue={`${metrics.requested} ${acronym}`}
                secondaryValue={` ${round(metrics.usage)} %`}
              />
              <MetricRow
                label="Used"
                progressValue={usedPercentage}
                progressColor="green.300"
                tooltipTitle={`Used ${acronym} out of Allocatable ${acronym}`}
                primaryValue={`${metrics.used} ${acronym}`}
                secondaryValue={` ${round(usedPercentage)} %`}
              />
            </>
          )}
        </Stack>
      ) : (
        <Skeleton width="100%" />
      )}
    </Stack>
  );
};
